/**
 * A pasted table, kept as a table.
 *
 * The editor cleans a paste down to what its own toolbar can make -- bold,
 * lists, links and lines; see `paste-html.ts` -- everywhere but the signature
 * editor. A table is none of those, and flattened to text it is not "the same
 * content without the styling": it is every cell run together
 * with a tab or a space between them, and a price list or a stock check pasted
 * into a reply arrived as a paragraph of numbers nobody could read across.
 *
 * So tables are the one structure lifted out of a paste, and rebuilt rather
 * than kept. Excel, Google Sheets, Word and web pages all put a `<table>` on the
 * clipboard, each wrapped in its own fonts, classes, widths and conditional
 * comments -- the very baggage plain-text paste exists to refuse. What is kept
 * is the grid: rows, cells, which cells are headers, and spans. Every cell's
 * contents become text. The styling is ours, inline, because email clients
 * ignore stylesheets and the server's sanitiser keeps `border`, `padding` and
 * `border-collapse` for exactly this.
 *
 * Deliberately no colours beyond the border. A header shaded light grey is
 * unreadable in the composer's dark theme, where the text inherited is light;
 * bold is enough to say which row is the heading and reads in both.
 *
 * The rest of the paste -- a sentence above a table copied out of an email,
 * say -- comes as plain text, with its line breaks, so taking the table does
 * not cost the words around it. It does cost their bold: a paste with a grid
 * in it is about the grid.
 */

/** What a pasted table looks like once it is ours. Inline: see the header. */
const TABLE_STYLE = "border-collapse: collapse; margin: 4px 0";
const CELL_STYLE = "border: 1px solid #98a2b3; padding: 4px 8px";
const HEADER_STYLE = `${CELL_STYLE}; font-weight: bold; text-align: left`;

/** Elements whose end is a line break when their text is read out. */
const BLOCKS = new Set([
  "P", "DIV", "LI", "TR", "H1", "H2", "H3", "H4", "H5", "H6",
  "BLOCKQUOTE", "PRE", "UL", "OL", "SECTION", "ARTICLE", "HEADER", "FOOTER",
]);

/**
 * The HTML to insert for this paste, or null to paste it as text as usual.
 *
 * Null whenever there is no table to keep, so the ordinary paths -- plain
 * text, and rich paste where it is allowed -- are untouched by this.
 */
export function tableFromClipboard(html: string, text: string): string | null {
  if (html && /<table[\s>]/i.test(html)) {
    const fromHtml = rebuild(html);
    if (fromHtml) return fromHtml;
  }
  return fromTabSeparated(text);
}

/** Rebuild the clipboard's HTML as clean tables and plain text around them. */
function rebuild(html: string): string | null {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const out: string[] = [];
  let line = "";
  let tables = 0;

  const flushLine = (): void => {
    if (line.trim() !== "") out.push(escapeHtml(line.trim()));
    line = "";
  };

  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      line += (node.textContent ?? "").replace(/\s+/g, " ");
      return;
    }
    if (!(node instanceof Element)) return;

    if (node.tagName === "TABLE") {
      const table = cleanTable(node as HTMLTableElement);
      if (table) {
        flushLine();
        out.push(table);
        tables += 1;
      }
      return;
    }
    if (node.tagName === "BR") {
      flushLine();
      return;
    }
    // Word and Excel put a <style> block and conditional comments in the body.
    if (node.tagName === "STYLE" || node.tagName === "SCRIPT") return;

    node.childNodes.forEach(walk);
    if (BLOCKS.has(node.tagName)) flushLine();
  };

  doc.body.childNodes.forEach(walk);
  flushLine();

  if (tables === 0) return null;

  /*
   * Text lines are joined with <br>, a table needs nothing either side of it,
   * and a trailing empty paragraph gives the caret somewhere to go: without
   * one, whatever the agent types next lands in the table's last cell.
   */
  return (
    out
      .map((part, index) =>
        index > 0 && !part.startsWith("<table") && !out[index - 1]!.startsWith("<table")
          ? `<br>${part}`
          : part,
      )
      .join("") + "<p><br></p>"
  );
}

/**
 * One table, with nothing kept but its grid.
 *
 * Rows are read through `rows` rather than by walking `tr`s, so a row inside
 * a nested table belongs to that table and not this one -- a web page's layout
 * table holding a data table would otherwise interleave the two. A nested table
 * is read out as its cell's text; tables inside tables are a page layout, not
 * something anybody wanted in a reply.
 */
function cleanTable(table: HTMLTableElement): string | null {
  const rows: string[] = [];

  for (const row of Array.from(table.rows)) {
    const cells = Array.from(row.cells).map((cell) => {
      const header = cell.tagName === "TH" || row.parentElement?.tagName === "THEAD";
      const tag = header ? "th" : "td";
      const spans =
        (cell.colSpan > 1 ? ` colspan="${cell.colSpan}"` : "") +
        (cell.rowSpan > 1 ? ` rowspan="${cell.rowSpan}"` : "");
      return `<${tag}${spans} style="${header ? HEADER_STYLE : CELL_STYLE}">${cellText(cell)}</${tag}>`;
    });
    if (cells.length > 0) rows.push(`<tr>${cells.join("")}</tr>`);
  }

  /*
   * An empty table -- spacer markup, or a single cell around an image that was
   * never going to survive plain paste -- is not worth a grid. Neither is one
   * cell, which is a box around some text and nothing a reader needs drawn.
   */
  const cellCount = table.querySelectorAll("td, th").length;
  if (rows.length === 0 || cellCount < 2) return null;

  return `<table style="${TABLE_STYLE}"><tbody>${rows.join("")}</tbody></table>`;
}

/** A cell's text, escaped, with its own line breaks kept as <br>. */
function cellText(cell: HTMLTableCellElement): string {
  const lines: string[] = [];
  let line = "";
  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      line += (node.textContent ?? "").replace(/\s+/g, " ");
      return;
    }
    if (!(node instanceof Element)) return;
    if (node.tagName === "STYLE" || node.tagName === "SCRIPT") return;
    if (node.tagName === "BR") {
      lines.push(line.trim());
      line = "";
      return;
    }
    node.childNodes.forEach(walk);
    if (BLOCKS.has(node.tagName)) {
      lines.push(line.trim());
      line = "";
    }
  };
  cell.childNodes.forEach(walk);
  lines.push(line.trim());

  const kept = lines.filter((value) => value !== "");
  // An empty cell still needs to hold its place in the grid, visibly.
  return kept.length > 0 ? kept.map(escapeHtml).join("<br>") : "&nbsp;";
}

/**
 * A table that arrived as text: tab-separated rows.
 *
 * What a spreadsheet puts on the clipboard as plain text, and the only form
 * some apps offer -- a terminal, a PDF viewer copying a grid, Excel over a
 * remote desktop. Taken only when it is unmistakably a grid: at least two
 * rows, every row with a tab, and every row the same width. Anything looser
 * catches prose that happens to contain a tab, and a sentence turned into a
 * one-row table is worse than a tab.
 *
 * No header row is assumed. Most spreadsheets do start with one, but bolding
 * a row of figures because it came first is a guess, and the agent can see
 * and fix the plain version where they could not see the guess.
 */
function fromTabSeparated(text: string): string | null {
  const rows = text.replace(/\r\n?/g, "\n").replace(/\n+$/, "").split("\n");
  if (rows.length < 2 || !rows.every((row) => row.includes("\t"))) return null;

  const grid = rows.map((row) => row.split("\t"));
  const width = grid[0]!.length;
  if (!grid.every((row) => row.length === width)) return null;

  const body = grid
    .map(
      (row) =>
        `<tr>${row
          .map((cell) => `<td style="${CELL_STYLE}">${cell.trim() ? escapeHtml(cell.trim()) : "&nbsp;"}</td>`)
          .join("")}</tr>`,
    )
    .join("");

  return `<table style="${TABLE_STYLE}"><tbody>${body}</tbody></table><p><br></p>`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
